import { useEffect, useMemo, useState } from 'react';
import type {
  DocumentRenderAttachmentSummary,
  DocumentRenderSection,
  ParsedTemplateEntry,
  TemplateEntryDef,
} from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { assertToolSucceeded } from '@/api/templates-api';
import { useAddEntry, useUpdateEntry } from '@/hooks/useTemplateQueries';
import {
  buildAddEntryInput,
  buildUpdateEntryInput,
  entryFormCanSubmit,
  initialEntryFormState,
  type EntryFormState,
} from './entry-form-state';
import { EntryFormFields } from './EntryFormFields';
import { entrySteps } from './entry-steps';
import { hasMatchingEntry } from './entry-write-guard';
import { useIsCompactViewport } from '@/hooks/useIsCompactViewport';
import { useCaptureWrite } from '@/hooks/useCaptureWrite';
import { InlineRetryError } from '@/components/ui/inline-retry-error';
import { getDocumentRender } from '@/api/content-api';
import { Loader2 } from 'lucide-react';

interface TemplateEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: DocumentRenderSection;
  def: TemplateEntryDef;
  instanceRootId: number;
  photos: DocumentRenderAttachmentSummary[];
  entry: ParsedTemplateEntry | null;
}

// The page's door for entries: the same form body the chat card uses, submitted through the
// same two tools; the tool validates every value, the dialog only shapes the call.
export function TemplateEntryDialog({
  open,
  onOpenChange,
  section,
  def,
  instanceRootId,
  photos,
  entry,
}: TemplateEntryDialogProps) {
  const [state, setState] = useState<EntryFormState>(() => initialEntryFormState(def, entry));
  const captureWrite = useCaptureWrite();
  const [pickerKey, setPickerKey] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const compact = useIsCompactViewport();
  const steps = useMemo(() => entrySteps(def), [def]);
  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const lastStep = safeIndex >= steps.length - 1;

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);
  const addEntry = useAddEntry();
  const updateEntry = useUpdateEntry();

  const pending = captureWrite.pending;
  const canSubmit = !pending && entryFormCanSubmit(def, state);

  const patch = (next: Partial<EntryFormState>) => {
    captureWrite.clear();
    setState((prev) => ({ ...prev, ...next }));
  };

  const reset = () => {
    setState(initialEntryFormState(def, null));
    captureWrite.clear();
    setPickerKey((value) => value + 1);
    setStepIndex(0);
  };

  const close = () => onOpenChange(false);

  const settle = (keepOpen: boolean) => (keepOpen ? reset() : close());

  const submit = (keepOpen: boolean) => {
    if (entry === null) {
      const input = buildAddEntryInput(def, state);
      void captureWrite.run({
        label: 'templates.addEntry',
        write: async () =>
          assertToolSucceeded(
            await addEntry.mutateAsync({ instanceId: instanceRootId, sectionCode: section.code, ...input })
          ),
        alreadyWritten: async () => {
          const fresh = await getDocumentRender(instanceRootId);
          const freshSection = fresh.sections.find((item) => item.code === section.code);
          return hasMatchingEntry(freshSection?.templateSection?.entries ?? [], input);
        },
        onWritten: () => settle(keepOpen),
      });
      return;
    }
    const input = buildUpdateEntryInput(def, state, entry);
    if (input === null) {
      settle(keepOpen);
      return;
    }
    void captureWrite.run({
      label: 'templates.updateEntry',
      write: async () =>
        assertToolSucceeded(
          await updateEntry.mutateAsync({
            instanceId: instanceRootId,
            sectionCode: section.code,
            entryNumber: entry.entryNumber,
            ...input,
          })
        ),
      onWritten: () => settle(keepOpen),
    });
  };

  const idPrefix = `entry-${section.id}-${entry?.entryNumber ?? 'new'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent compactFullScreen>
        <DialogHeader>
          <DialogTitle>{entry === null ? `Add ${def.itemLabel}` : `Edit ${def.itemLabel}`}</DialogTitle>
          <DialogDescription>
            {entry === null
              ? `One ${def.itemLabel} in ${section.title || section.code}. Fields left empty stay empty.`
              : `Entry ${entry.entryNumber} in ${section.title || section.code}. Only what you change is saved.`}
          </DialogDescription>
        </DialogHeader>
        {compact ? (
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {`${safeIndex + 1} of ${steps.length}`} · {steps[safeIndex].label}
          </p>
        ) : null}
        <DialogBody className="space-y-3">
          <EntryFormFields
            def={def}
            state={state}
            onChange={patch}
            sectionId={section.id}
            photos={photos}
            existingPhotoCodes={entry?.photoCodes ?? []}
            capture
            disabled={pending}
            idPrefix={idPrefix}
            pickerKey={pickerKey}
            {...(compact ? { step: steps[safeIndex] } : {})}
          />
          {captureWrite.error !== null ? (
            <InlineRetryError message={captureWrite.error} onRetry={captureWrite.retry} disabled={pending} />
          ) : null}
        </DialogBody>
        {compact ? (
          <div className="sticky bottom-0 -mx-4 -mb-4 flex gap-2 border-t bg-background px-4 pb-safe pt-3">
            <Button
              variant="outline"
              className="h-12 flex-1"
              onClick={() => (safeIndex === 0 ? close() : setStepIndex(safeIndex - 1))}
              disabled={pending}
            >
              {safeIndex === 0 ? 'Cancel' : 'Back'}
            </Button>
            {lastStep ? (
              <>
                {entry === null ? (
                  <Button
                    variant="secondary"
                    className="h-12 flex-1"
                    onClick={() => submit(true)}
                    disabled={!canSubmit}
                  >
                    {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save and add another
                  </Button>
                ) : null}
                <Button className="h-12 flex-1" onClick={() => submit(false)} disabled={!canSubmit}>
                  {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
                </Button>
              </>
            ) : (
              <Button className="h-12 flex-1" onClick={() => setStepIndex(safeIndex + 1)} disabled={pending}>
                Next
              </Button>
            )}
          </div>
        ) : (
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={pending}>
              Cancel
            </Button>
            {entry === null ? (
              <Button variant="secondary" onClick={() => submit(true)} disabled={!canSubmit}>
                {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save and add another
              </Button>
            ) : null}
            <Button onClick={() => submit(false)} disabled={!canSubmit}>
              {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
